import React from "react"
import { useTranslation } from "react-i18next"

import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { HStack } from "@/nzds/layout"
import type { UploadFileType } from "@/utils/upload-file-type"
import { filesize } from "filesize"

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material"

import { UploadFile } from "../upload-file"

// Mirrors DataManager::UploadHandler::MAX_FILE_SIZE (10 MB) on the backend
const MAX_UPLOAD_FILE_SIZE_BYTES = 10 * 1024 * 1024

interface UploadFilesDialogProps {
  acceptedFileTypes: UploadFileType[]
  isSubmitting?: boolean
  multiple?: boolean
  onClose: () => void
  onUpload: (files: File[]) => void
  open: boolean
  title: string
}

export const UploadFilesDialog: React.FC<UploadFilesDialogProps> = ({
  acceptedFileTypes,
  isSubmitting,
  multiple = false,
  onClose,
  onUpload,
  open,
  title,
}) => {
  const { t } = useTranslation("common")
  const [files, setFiles] = React.useState<File[]>([])
  const supportedFileTypes = acceptedFileTypes
    .map((type) => type.toUpperCase())
    .join(", ")

  const handleClose = (): void => {
    setFiles([])
    onClose()
  }

  const handleUpload = (): void => {
    if (files.length === 0) {
      return
    }

    onUpload(files)
    setFiles([])
  }

  return (
    <Dialog
      aria-describedby="upload-dialog-description"
      aria-labelledby="upload-dialog-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
    >
      <DialogTitle id="upload-dialog-title" onClose={handleClose}>
        {title}
      </DialogTitle>
      <DialogContent id="upload-dialog-description">
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <UploadFile
            acceptedFileTypes={acceptedFileTypes}
            isDisabled={isSubmitting}
            multiple={multiple}
            onChange={setFiles}
          />
          <HStack sx={{ justifyContent: "space-between" }}>
            <Typography variant="body2">
              {t("upload.supportedFileType")}: {supportedFileTypes}
            </Typography>
            <Typography variant="body2">
              {t("upload.maxFileSize")}:{" "}
              {filesize(MAX_UPLOAD_FILE_SIZE_BYTES, { standard: "jedec" })}
            </Typography>
          </HStack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={files.length === 0 || isSubmitting}
          loading={isSubmitting}
          onClick={handleUpload}
          variant="contained"
        >
          {t("button.upload")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
